import { useEffect, useRef } from "react";
import type { ReactNode } from "react";

// Shared with the result rows, which show the same address line.
export {
  HcbbDetailAddress,
  HcbbDetailLine,
} from "@/pages/components/hcbb-cost-estimator/hcbb-address-line";

/** Ties the screen's landmark to its heading. */
export const HCBB_DETAIL_HEADING_ID = "hcbb-detail-heading";

/**
 * The block both detail screens open with: who this is, how to reach them, and
 * what the row said about them.
 *
 * Focus moves to the heading on arrival. A route change in this app announces
 * nothing and leaves focus where it was, so without this a keyboard or screen
 * reader user lands back at the top of the app chrome and has to walk down to
 * the content they just asked for.
 */
export const HcbbDetailHeader = ({
  title,
  leading,
  badge,
  actions,
  children,
}: {
  title: string;
  /** Left of the title — the provider's photo. */
  leading?: ReactNode;
  /** Beside the title — the specialty pill. */
  badge?: ReactNode;
  /** The right-hand column: availability and the phone button. */
  actions: ReactNode;
  /** The icon lines under the title. */
  children: ReactNode;
}) => {
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  return (
    <div className="flex items-start justify-between gap-4">
      <div className="flex min-w-0 items-center gap-4">
        {leading}

        <div className="min-w-0 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            {/* Wraps rather than truncates: a clipped page heading loses
                content with nothing to recover it from. */}
            <h1
              ref={headingRef}
              id={HCBB_DETAIL_HEADING_ID}
              tabIndex={-1}
              className="break-words text-large font-bold text-gray-800 focus-visible:outline-none"
            >
              {title}
            </h1>
            {badge}
          </div>

          {children}
        </div>
      </div>

      {/* Never squeezed by a long name beside it. */}
      <div className="flex shrink-0 flex-col items-end gap-2">{actions}</div>
    </div>
  );
};
